import React from 'react';

import pick from 'lodash/pick';

import { useClassnames } from '@lumapps/classnames';
import { get } from '@lumapps/constants';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Size, UserBlock as LumXUserBlock, UserBlockProps as LumXUserBlockProps, Avatar } from '@lumapps/lumx/react';
import { useDisabledStateContext } from '@lumapps/lumx/react/utils';
import { Link as RouterLink } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';

import { DEFAULT_AVATAR } from '../../constants';
import { useUser } from '../../hooks/useUser';
import { USER } from '../../keys';
import { BaseUserComponentProps } from '../../types';
import { isUserDeleted } from '../../utils/isUserDeleted';
import { isUserInactive } from '../../utils/isUserInactive';
import { UserAvatar, type UserAvatarProps } from '../UserAvatar';
import { useUserCardPopover, UserCardPopoverProps } from '../UserCardPopover';
import { UserCardPopoverWrapper, UserCardPopoverWrapperProps } from '../UserCardPopover/Wrapper';
import { UserBlockSkeleton, UserBlockSkeletons, type UserBlockSkeletonProps } from './UserBlockSkeleton';

import './index.scss';

/** Base UserBlock LumX props. */
type BaseUserBlockProps = Pick<
    LumXUserBlockProps,
    'multipleActions' | 'onMouseLeave' | 'onMouseEnter' | 'orientation' | 'size' | 'theme' | 'nameProps'
>;

export interface UserBlockProps
    extends BaseUserBlockProps,
        BaseUserComponentProps,
        Pick<UserCardPopoverWrapperProps, 'isInline' | 'triggerProps'>,
        Omit<
            UserCardPopoverProps,
            | 'user'
            | 'isLoading'
            | 'showUserCard'
            | 'anchorRef'
            | 'onClick'
            | 'onMouseLeave'
            | 'onMouseEnter'
            | 'isDetailsOpen'
            | 'asDialog'
        > {
    /**
     * whether the user name should redirect to the user's profile.
     * This forces the user card not to be displayed on hover as well
     */
    shouldNameRedirectToUserProfile?: boolean;
    /** fields to be displayed below the default user block fields */
    afterFields?: LumXUserBlockProps['after'];
    /** whether the user avatar should be displayed or not */
    shouldDisplayAvatar?: boolean;
    /** additional avatar props */
    avatarProps?: Partial<LumXUserBlockProps['avatarProps']>;
    /** override for the primary fields to display, ignores props `fields` and `additionalFields` */
    primaryFields?: string[];
    /** children to display, will override user full name */
    children?: React.ReactNode;
    /** whether inactive users (disabled & deleted) should be flagged or not */
    shouldFlagInactiveUsers?: boolean;
    /** whether the block is loading or not */
    isLoading?: boolean;
    /** whether to display letter bubble instead or regular avatar */
    shouldUseColorBubbleLetter?: boolean;
    /** props to forward to letter bubble avatar */
    colorBubbleLetterProps?: Partial<UserAvatarProps>;
    /** props to know the account type of the displayed user */
    isExternalAccount?: boolean;
}

const CLASSNAME = 'user-block';
const { isWebComponentsApp } = get();

/**
 * Component that displays the a User in a block format. Can display the user avatar, the user name and
 * additional fields. It difers from the `UserLink` component since it is a block element and can display
 * additional information about the user, and it cannot be displayed inline.
 *
 * @family Blocks
 * @param {UserBlockProps} props - The props of the component
 * @returns UserBlock
 */
export const UserBlock = React.forwardRef<HTMLDivElement, UserBlockProps>((props, ref) => {
    const {
        user,
        showUserCardOnHover = true,
        scope,
        shouldNameRedirectToUserProfile = true,
        isExternalAccount,
        size = Size.m,
        afterFields,
        fields,
        primaryFields,
        shouldDisplayAvatar = true,
        fallbackAvatar = DEFAULT_AVATAR,
        avatarProps: additionalAvatarProps,
        userCardOnHoverDelay,
        isLoading: isBlockLoading = false,
        theme,
        children,
        renderUserFullName,
        additionalFields,
        shouldFlagInactiveUsers = false,
        shouldUseColorBubbleLetter,
        colorBubbleLetterProps,
        ...restOfProps
    } = props;
    const { pluralize, translateAndReplace, translateAndReplaceWithComponents } = useTranslate();

    const isDisabledByContext = useDisabledStateContext().state === 'disabled';
    // Inactive users are users that are either disabled or deleted.
    const isInactive = shouldFlagInactiveUsers && isUserInactive(user);
    const isDeleted = shouldFlagInactiveUsers && isUserDeleted(user);

    /**
     * If the name should not redirect to the user profile, we should not show the user card on hover since
     * there is nothing to focus the user card on. No anchor means to no user card to anchor to.
     */
    let shouldShowUserCardOnHover =
        !shouldNameRedirectToUserProfile && !isExternalAccount ? false : showUserCardOnHover;

    /** Never show the user card on web components due to the complexity of the feature */
    if (isWebComponentsApp || isDisabledByContext) {
        shouldShowUserCardOnHover = false;
    }

    const { avatarProps, userProfile, getUserFullName, userFields } = useUser(user, {
        ...restOfProps,
        additionalFields,
        fields,
    });

    const { get } = useDataAttributes(scope ? `${scope}-user` : 'user');
    const { block } = useClassnames(CLASSNAME);

    const {
        onMouseEnter,
        onMouseLeave,
        user: fetchedUser,
        isLoading,
        showUserCard,
        anchorRef,
        asDialog,
        onClick: onUserCardTriggerClick,
    } = useUserCardPopover(user, {
        ...restOfProps,
        additionalFields,
        delay: userCardOnHoverDelay,
        shouldUseTrigger: true,
        targetClassname: ['lumx-user-block__field', 'lumx-link', CLASSNAME],
    });

    const fullname = React.useMemo(() => {
        // When deleting a user, the backend set his name to "Unknown User", we change this to a translated "Inactive user" string
        if (isDeleted) {
            return pluralize(USER.INACTIVE_USERS, 1);
        }

        const userFullName = children || renderUserFullName?.(getUserFullName()) || getUserFullName();

        // If the user is inactive, we append a suffix " (inactive)" to the user's full name
        if (isInactive) {
            if (React.isValidElement(userFullName)) {
                return translateAndReplaceWithComponents(USER.INACTIVE_USER_SUFFIX, {
                    USER_NAME: userFullName,
                });
            }
            return translateAndReplace(USER.INACTIVE_USER_SUFFIX, {
                USER_NAME: userFullName.toString(),
            });
        }
        return userFullName;
    }, [
        children,
        getUserFullName,
        isDeleted,
        isInactive,
        pluralize,
        renderUserFullName,
        translateAndReplace,
        translateAndReplaceWithComponents,
    ]);

    const linkProps = {
        to: userProfile.route,
        ...get({ element: 'block-link', action: 'name' }),
    } as any;

    const fieldsToDisplay = primaryFields || userFields || [];
    const hasFieldsToDisplay = fieldsToDisplay.length > 0 || Boolean(afterFields);

    if (isBlockLoading) {
        return <UserBlockSkeleton size={size} {...restOfProps} />;
    }

    return (
        <UserCardPopoverWrapper
            // Don't show the user card if the user is deleted
            showUserCardOnHover={shouldShowUserCardOnHover && !isDeleted}
            showUserCard={showUserCard && !isDeleted}
            anchorRef={anchorRef}
            onClick={onUserCardTriggerClick}
            onMouseLeave={onMouseLeave}
            onMouseEnter={onMouseEnter}
            user={fetchedUser}
            isLoading={isLoading}
            asDialog={asDialog}
            {...restOfProps}
            triggerProps={{
                ...restOfProps.triggerProps,
                theme,
            }}
            wrapperProps={{
                gap: fieldsToDisplay?.length > 0 && size !== Size.s && size !== Size.xs ? 'big' : undefined,
                ref,
                ...restOfProps.wrapperProps,
            }}
            userBlockProps={{
                ...restOfProps.userBlockProps,
                avatarProps: {
                    thumbnailProps: {
                        ...additionalAvatarProps?.userBlockProps?.avatarProps?.thumbnailProps,
                        // eslint-disable-next-line deprecation/deprecation
                        fallback: <Avatar size={Size.l} image={fallbackAvatar} alt="" />,
                        ...avatarProps?.thumbnailProps,
                    },
                },
            }}
        >
            {/* eslint-disable-next-line deprecation/deprecation */}
            <LumXUserBlock
                {...get({ element: 'block' })}
                {...pick(restOfProps, ['data-id', 'multipleActions', 'orientation', 'theme'])}
                className={block({ 'has-fields': hasFieldsToDisplay }, [restOfProps.className])}
                nameProps={{
                    ...restOfProps.nameProps,
                    ref: !asDialog ? anchorRef : undefined,
                    onMouseEnter: shouldShowUserCardOnHover ? onMouseEnter : undefined,
                    onMouseLeave,
                }}
                size={size}
                name={fullname}
                additionalFields={afterFields}
                theme={theme}
                avatarProps={
                    shouldDisplayAvatar
                        ? {
                              linkProps:
                                  shouldNameRedirectToUserProfile && !isDisabledByContext
                                      ? {
                                            ...linkProps,
                                            ...get({ element: 'block-link', action: 'avatar' }),
                                            'aria-hidden': fullname ? true : undefined,
                                        }
                                      : undefined,
                              ...avatarProps,
                              image: shouldUseColorBubbleLetter ? undefined : avatarProps?.image || linkProps?.image,
                              ...additionalAvatarProps,
                              thumbnailProps: {
                                  // eslint-disable-next-line deprecation/deprecation
                                  fallback: shouldUseColorBubbleLetter ? (
                                      <UserAvatar
                                          user={user}
                                          size={size}
                                          {...colorBubbleLetterProps}
                                          useColorBubbleLetter
                                      />
                                  ) : (
                                      <Avatar size={size} image={fallbackAvatar} alt="" />
                                  ),
                                  ...avatarProps?.thumbnailProps,
                                  ...additionalAvatarProps?.thumbnailProps,
                              },
                          }
                        : undefined
                }
                /** @ts-expect-error wrong type on the DS side */
                fields={fieldsToDisplay.length > 0 ? fieldsToDisplay : undefined}
                // Don't add a profile link if the user is deleted
                linkAs={shouldNameRedirectToUserProfile && !isDeleted && RouterLink}
                linkProps={
                    shouldNameRedirectToUserProfile && !isDisabledByContext && !isDeleted ? linkProps : undefined
                }
            />
        </UserCardPopoverWrapper>
    );
});

UserBlock.displayName = 'UserBlock';

export { UserBlockSkeletons, UserBlockSkeleton };
export type { UserBlockSkeletonProps };
